import { cn } from '../lib/utils';
import { Relation, TC } from '@cotera/era';
import { timeSeries } from '@cotera/client/app/components/utils/format/time-series';
import { UTCDate } from '@cotera/client/app/components/utils/utils';
import { useKeyPress } from '@cotera/client/app/hooks/use-key-press';
import React, { useCallback, forwardRef } from 'react';
import _ from 'lodash';
import { CodeBlock } from '@cotera/client/app/components';

export const DefaultDetailViewCell = forwardRef<
  HTMLDivElement,
  {
    className?: string;
    children: React.ReactNode;
    title: string | React.ReactNode;
    focused: boolean;
  }
>(({ children, className, title, focused }, ref) => {
  return (
    <>
      <div
        ref={ref}
        className={cn(
          'text-sm font-medium flex items-center',
          focused && 'text-indigo-500'
        )}
      >
        {title}
      </div>
      <div
        className={cn(
          'h-full px-3 py-2 text-sm flex items-center border border-border rounded-md break-all whitespace-pre-wrap',
          className
        )}
      >
        {children}
      </div>
    </>
  );
});

export const DetailView: React.FC<{
  rowData: Record<string, any>;
  rel: Relation;
  renderer?: React.ForwardRefExoticComponent<
    {
      column: string;
      value: any;
      rel: Relation;
      focused: boolean;
      rowData?: Record<string, any>;
      rowIndex?: number;
    } & React.RefAttributes<HTMLDivElement>
  >;
  col: string;
  onKeyDown?: (e: KeyboardEvent) => void;
  rowIndex?: number;
}> = ({
  rowData,
  rel,
  renderer: Renderer,
  onKeyDown,
  col: focusedCol,
  rowIndex,
}) => {
  const cols = Object.keys(rowData);

  // Use callback ref to scroll into view when the ref is attached
  // This is more robust than useEffect as it triggers immediately when the DOM element is ready
  const focusedCellRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (node) {
        node.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      }
    },
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [focusedCol]
  );

  useKeyPress(
    (e) => ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key),
    (e) => {
      onKeyDown?.(e);
    }
  );

  return (
    <div className="grid grid-cols-2 gap-4 max-h-[80vh] overflow-y-auto">
      {cols.map((col) => {
        const isFocused = col === focusedCol;

        if (Renderer !== undefined) {
          return (
            <Renderer
              key={col}
              ref={isFocused ? focusedCellRef : undefined}
              focused={isFocused}
              column={col}
              value={rowData[col]}
              rel={rel}
              rowData={rowData}
              rowIndex={rowIndex}
            />
          );
        }
        return (
          <DetailViewCell
            key={col}
            ref={isFocused ? focusedCellRef : undefined}
            focused={isFocused}
            column={col}
            value={rowData[col]}
            rel={rel}
          />
        );
      })}
    </div>
  );
};

export const DetailViewCell = forwardRef<
  HTMLDivElement,
  {
    column: string;
    value: any;
    rel: Relation;
    focused: boolean;
  }
>(({ column, value, rel, focused }, ref) => {
  const ty = rel.attributes[column]!;

  if (TC.isStruct(ty) || TC.isArray(ty)) {
    return (
      <DefaultDetailViewCell ref={ref} title={column} focused={focused}>
        <CodeBlock
          language="json"
          code={JSON.stringify(value, null, 2)}
          bordered={false}
        />
      </DefaultDetailViewCell>
    );
  }

  if (TC.isTimestamp(ty)) {
    return (
      <DefaultDetailViewCell ref={ref} title={column} focused={focused}>
        {timeSeries(new UTCDate(value), 'day')}
      </DefaultDetailViewCell>
    );
  }

  if (TC.isBoolean(ty)) {
    return (
      <DefaultDetailViewCell ref={ref} title={column} focused={focused}>
        {String(value)}
      </DefaultDetailViewCell>
    );
  }

  // Runtime check for object/array values to handle cases where type isn't properly detected
  // This prevents "[object Object]" from being displayed
  // Use CodeBlock for better formatting in detail view
  if (value !== null && typeof value === 'object' && !(value instanceof Date)) {
    return (
      <DefaultDetailViewCell ref={ref} title={column} focused={focused}>
        <CodeBlock
          language="json"
          code={JSON.stringify(value, null, 2)}
          bordered={false}
        />
      </DefaultDetailViewCell>
    );
  }

  return (
    <DefaultDetailViewCell
      ref={ref}
      focused={focused}
      className=""
      title={column}
    >
      {value}
    </DefaultDetailViewCell>
  );
});
